import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/EnterpriseAuthContext';
import { toast } from 'sonner';
import * as Sentry from '@sentry/react';
import { retryWithBackoff } from '@/lib/retry-utils';
import { mapError, getErrorMessage } from '@/lib/api-errors';
import { validateInput, profileUpdateSchema } from '@/lib/validation-schemas';
import { useOfflineQueue } from '@/hooks/useOfflineQueue';
import { RateLimiters } from '@/lib/rate-limiter';
import { measureOperation } from '@/lib/performance-monitor';
import {
  PROFILE_QUERY_KEYS,
  PROFILE_ERRORS,
  type UserProfile,
  type ProfileUpdatePayload,
} from './shared';

/**
 * Hook for profile update and delete mutations
 * 
 * Features:
 * - Optimistic updates with automatic rollback on error
 * - Offline queue integration for transient errors
 * - Input validation
 * - Comprehensive error handling
 * 
 * @returns Mutation functions and state
 */
export function useProfileMutations() {
  const { user } = useAuth();
  const queryClient = useQueryClient();
  const { addToQueue } = useOfflineQueue();

  /**
   * Mutation for updating profile data
   * Includes optimistic updates and error recovery
   */
  const updateMutation = useMutation({
    mutationFn: async (profileData: ProfileUpdatePayload) => {
      if (!user?.id) {
        throw new Error(PROFILE_ERRORS.NOT_AUTHENTICATED);
      }

      // Rate limit profile updates (10 per minute)
      const rateCheck = RateLimiters.profileUpdate.check();
      if (!rateCheck.allowed) {
        throw new Error(
          `Too many profile updates. Please wait ${Math.ceil((rateCheck.retryAfter || 0) / 1000)} seconds.`
        );
      }
      RateLimiters.profileUpdate.record();

      // Validate input data
      const validation = validateInput(profileUpdateSchema, profileData);
      if (!validation.valid) {
        throw mapError(validation.errors, 'auth');
      }

      // Measure API performance
      return await measureOperation(
        'profile-update',
        async () => {
          return await retryWithBackoff(
            async () => {
              const { data, error } = await supabase
                .from('profiles')
                .update(profileData)
                .eq('user_id', user.id)
                .select()
                .single();

              if (error) {
                throw mapError(error, 'database');
              }

              return data as unknown as UserProfile;
            },
            { maxRetries: 3, baseDelay: 1000 }
          );
        },
        'api',
        { userId: user.id }
      );
    },
    onMutate: async (profileData) => {
      if (!user?.id) return;

      const queryKey = PROFILE_QUERY_KEYS.detail(user.id);

      // Cancel outgoing refetches to avoid overwriting optimistic update
      await queryClient.cancelQueries({ queryKey });

      // Snapshot current value for potential rollback
      const previousProfile = queryClient.getQueryData<UserProfile>(queryKey);

      // Optimistically update cache with new data
      queryClient.setQueryData<UserProfile | null>(queryKey, (old) => {
        if (!old) return null;
        return {
          ...old,
          ...profileData,
          updated_at: new Date().toISOString(),
        };
      });

      // Return context with snapshot for error recovery
      return { previousProfile };
    },
    onError: (error, profileData, context) => {
      // Rollback optimistic update on error
      if (context?.previousProfile && user?.id) {
        queryClient.setQueryData(
          PROFILE_QUERY_KEYS.detail(user.id),
          context.previousProfile
        );
      }

      // Map error and handle offline queue
      const apiError = mapError(error, 'database');

      if (apiError.isTransient && addToQueue && user?.id) {
        // Queue for offline sync if error is transient (network issues, timeouts)
        addToQueue(
          'generic',
          'profile_update',
          { userId: user.id, updates: profileData },
          profileData,
          'Update profile',
          'medium'
        );

        toast.info('Changes saved locally - will sync when connection is restored');
      } else {
        // Show error message for non-transient errors
        toast.error(getErrorMessage(error));
      }

      // Log error to Sentry for monitoring
      Sentry.captureException(error, {
        tags: {
          context: 'profile_error',
          operation: 'user_profile',
          action: 'update',
        },
        extra: {
          message: PROFILE_ERRORS.UPDATE_FAILED,
          profileData,
        },
      });
    },
    onSuccess: (data) => {
      if (!user?.id) return;

      // Update cache with server response (source of truth)
      queryClient.setQueryData(PROFILE_QUERY_KEYS.detail(user.id), data);

      // Invalidate related queries to keep everything in sync
      queryClient.invalidateQueries({
        queryKey: PROFILE_QUERY_KEYS.all,
      });

      toast.success('Profile updated successfully');
    },
  });

  /**
   * Mutation for deleting a profile
   * Requires permission check via RPC before deletion
   */
  const deleteMutation = useMutation({
    mutationFn: async (targetUserId: string) => {
      if (!user?.id) {
        throw new Error(PROFILE_ERRORS.NOT_AUTHENTICATED);
      }

      return await retryWithBackoff(
        async () => {
          // Check permissions first via Supabase RPC
          const { data: canDelete, error: permissionError } = await supabase.rpc(
            'can_delete_profile',
            { target_user_id: targetUserId }
          );

          if (permissionError) {
            throw mapError(permissionError, 'database');
          }

          if (!canDelete) {
            throw new Error(PROFILE_ERRORS.PERMISSION_DENIED);
          }

          // Perform deletion
          const { error } = await supabase
            .from('profiles')
            .delete()
            .eq('user_id', targetUserId);

          if (error) {
            throw mapError(error, 'database');
          }
        },
        { maxRetries: 2, baseDelay: 1000 }
      );
    },
    onSuccess: (_, targetUserId) => {
      // Remove deleted profile from cache
      queryClient.removeQueries({
        queryKey: PROFILE_QUERY_KEYS.detail(targetUserId),
      });

      // Invalidate profile lists
      queryClient.invalidateQueries({
        queryKey: ['profiles'],
      });

      toast.success('Profile deleted successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));

      Sentry.captureException(error, {
        tags: {
          context: 'profile_error',
          operation: 'delete_profile',
        },
        extra: { message: PROFILE_ERRORS.DELETE_FAILED },
      });
    },
  });

  /**
   * Helper function to update profile with callback interface
   */
  const updateProfile = useCallback(
    (data: ProfileUpdatePayload) => {
      return updateMutation.mutateAsync(data);
    },
    [updateMutation]
  );

  /**
   * Helper function to delete profile with callback interface
   */
  const deleteProfile = useCallback(
    (targetUserId: string) => {
      return deleteMutation.mutateAsync(targetUserId);
    },
    [deleteMutation]
  );

  return {
    // Mutation functions
    updateProfile,
    deleteProfile,
    
    // Mutation state
    isUpdating: updateMutation.isPending,
    isDeleting: deleteMutation.isPending,
    updateError: updateMutation.error,
    deleteError: deleteMutation.error,
    
    // Raw mutations (for advanced usage)
    updateMutation,
    deleteMutation,
  };
}

/**
 * Legacy hook for backward compatibility
 * Use useProfileMutations().deleteProfile for new code
 * 
 * @param targetUserId - ID of profile to delete
 * @returns Mutation for deleting a profile
 */
export function useDeleteProfile(targetUserId?: string) {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (reason?: string) => {
      if (!user?.id) {
        throw new Error(PROFILE_ERRORS.NOT_AUTHENTICATED);
      }
      if (!targetUserId) {
        throw new Error('Target user ID is required');
      }

      return await retryWithBackoff(
        async () => {
          // Check permissions first via Supabase RPC
          const { data: canDelete, error: permissionError } = await supabase.rpc(
            'can_delete_profile',
            { target_user_id: targetUserId }
          );

          if (permissionError) {
            throw mapError(permissionError, 'database');
          }

          if (!canDelete) {
            throw new Error(PROFILE_ERRORS.PERMISSION_DENIED);
          }

          // Perform deletion
          const { error } = await supabase
            .from('profiles')
            .delete()
            .eq('user_id', targetUserId);

          if (error) {
            throw mapError(error, 'database');
          }
        },
        { maxRetries: 2, baseDelay: 1000 }
      );
    },
    onSuccess: () => {
      // Remove deleted profile from cache
      queryClient.removeQueries({
        queryKey: PROFILE_QUERY_KEYS.detail(targetUserId || ''),
      });

      // Invalidate profile lists
      queryClient.invalidateQueries({
        queryKey: ['profiles'],
      });

      toast.success('Profile deleted successfully');
    },
    onError: (error) => {
      toast.error(getErrorMessage(error));

      Sentry.captureException(error, {
        tags: {
          context: 'profile_error',
          operation: 'delete_profile',
        },
        extra: { message: PROFILE_ERRORS.DELETE_FAILED },
      });
    },
  });
}
